import { repApi, qs } from './repApi';
import type { HistoryEntry, Lcert, NotificationBody, NotificationContact } from '@/types';

export const lcertApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    listLcert: builder.query<Lcert[], { county?: number; year?: string | number; state?: boolean }>(
      {
        query: ({ county, year, state }) =>
          `/lcert${qs({ county, year, state: state ? 1 : undefined })}`,
        providesTags: (result) => [
          'Lcert',
          ...(result || []).map((r) => ({ type: 'Lcert' as const, id: r.pvDataID })),
        ],
      },
    ),
    lcertYears: builder.query<(string | number)[], void>({
      query: () => '/lcert/years',
      providesTags: ['LcertYears'],
    }),
    getLcert: builder.query<Lcert, number>({
      query: (id) => `/lcert/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Lcert', id }],
    }),
    lcertHistory: builder.query<HistoryEntry[], number>({
      query: (id) => `/lcert/${id}/history`,
      keepUnusedDataFor: 0,
    }),
    updateLcert: builder.mutation<Lcert, { id: number; body: Partial<Lcert> }>({
      query: ({ id, body }) => ({ url: `/lcert/${id}`, method: 'PUT', body }),
      invalidatesTags: (r, _e, { id }) => [
        { type: 'Lcert', id },
        'Lcert',
        ...(r ? ['Groups' as const] : []),
      ],
    }),
    submitLcert: builder.mutation<{ letter: Lcert; notificationsQueued: number }, number>({
      query: (id) => ({ url: `/lcert/${id}/submit`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Lcert', id }, 'Lcert', 'Groups'],
    }),
    approveLcert: builder.mutation<Lcert, number>({
      query: (id) => ({ url: `/lcert/${id}/approve`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Lcert', id }, 'Lcert', 'Groups'],
    }),
    requestLcertChanges: builder.mutation<
      Lcert,
      { id: number; reasons: string[]; comment?: string }
    >({
      query: ({ id, reasons, comment }) => ({
        url: `/lcert/${id}/request-changes`,
        method: 'POST',
        body: { reasons, comment },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lcert', id }, 'Lcert', 'Groups'],
    }),
    changeLcertYear: builder.mutation<
      { pvYear: string; created: number },
      { pvYear: string | number; serviceCounties?: string[] }
    >({
      query: (body) => ({ url: '/lcert/change-year', method: 'POST', body }),
      invalidatesTags: ['Lcert', 'LcertYears', 'Groups'],
    }),
    // Notifications (state contacts, org 900; triggers = county names)
    lcertNotifications: builder.query<NotificationContact[], void>({
      query: () => '/lcert/notifications',
      providesTags: ['LcertNotifications'],
    }),
    getLcertNotification: builder.query<NotificationContact, number>({
      query: (id) => `/lcert/notifications/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'LcertNotifications', id }],
    }),
    createLcertNotification: builder.mutation<NotificationContact, NotificationBody>({
      query: (body) => ({ url: '/lcert/notifications', method: 'POST', body }),
      invalidatesTags: ['LcertNotifications'],
    }),
    updateLcertNotification: builder.mutation<
      NotificationContact,
      { id: number; body: NotificationBody }
    >({
      query: ({ id, body }) => ({ url: `/lcert/notifications/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        'LcertNotifications',
        { type: 'LcertNotifications', id },
      ],
    }),
    deleteLcertNotification: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/lcert/notifications/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LcertNotifications'],
    }),
  }),
});

export const {
  useListLcertQuery,
  useLazyListLcertQuery,
  useLcertYearsQuery,
  useGetLcertQuery,
  useLcertHistoryQuery,
  useUpdateLcertMutation,
  useSubmitLcertMutation,
  useApproveLcertMutation,
  useRequestLcertChangesMutation,
  useChangeLcertYearMutation,
  useLcertNotificationsQuery,
  useGetLcertNotificationQuery,
  useCreateLcertNotificationMutation,
  useUpdateLcertNotificationMutation,
  useDeleteLcertNotificationMutation,
} = lcertApi;
